<template>
  <div class="slider" :class="{ merged: isMerged }">
    <div ref="slider-line" class="slider-line">
      <div
        ref="first-dot"
        class="slider-dot-container"
        :class="{ dragging: dots.firstDot.isDraggingStart }"
        :style="{ transform: `translateX(${dots.firstDot.xPad}px)` }"
        @mousedown="draggingStart('firstDot', $event)"
        @touchstart="draggingStart('firstDot', $event)"
      >
        <div
          class="slider-dot"
          role="slider"
          :aria-valuemin="minVal"
          :aria-valuenow="dots.firstDot.currentVal"
          :aria-valuemax="maxVal"
          :aria-label="ariaLabel"
        ></div>
        <div ref="first-dot-slider-tooltip" class="slider-tooltip">
          {{ processDotLabel('firstDot') }}
        </div>
      </div>
      <div
        class="slider-process"
        :style="{
          width: `${slider.width}px`,
          left: `${slider.leftPad}px`
        }"
      >
        <div class="slider-tooltip tooltip-merge">
          {{ processMergedLabel() }}
        </div>
      </div>
      <div
        ref="last-dot"
        class="slider-dot-container"
        :class="{ dragging: dots.lastDot.isDraggingStart }"
        :style="{ transform: `translateX(${dots.lastDot.xPad}px)` }"
        @mousedown="draggingStart('lastDot', $event)"
        @touchstart="draggingStart('lastDot', $event)"
      >
        <div
          class="slider-dot"
          role="slider"
          :aria-valuemin="minVal"
          :aria-valuenow="dots.lastDot.currentVal"
          :aria-valuemax="maxVal"
          :aria-label="ariaLabel"
        ></div>
        <div ref="last-dot-slider-tooltip" class="slider-tooltip">
          {{ processDotLabel('lastDot') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StepValues from './step-values/step-values'

export default {
  name: 'RangeSlider',
  props: {
    /**
     * Minimum val for the range
     *
     * @type {number}
     */
    minVal: {
      type: Number,
      required: true
    },
    /**
     * Maximum val for the range
     *
     * @type {number}
     */
    maxVal: {
      type: Number,
      required: true
    },
    /**
     * Step value for range
     *
     * @type {number}
     */
    step: {
      type: Number,
      required: true
    },
    /**
     * Prefix label on both button
     *
     * @type {string}
     */
    labelPrefix: {
      type: String,
      default: ''
    },
    /**
     * Suffix label on both button
     *
     * @type {string}
     */
    labelSuffix: {
      type: String,
      default: ''
    },
    /**
     * ID of element that labels this slider
     * For accessibility purpose
     *
     * @type {string}
     */
    ariaLabel: {
      type: String,
      required: true
    },
    /**
     * locale
     * Format: language_countryCodeInUppercase (eg: en_ID) or just country code (uppercase / lowercase)
     * Giving an invalid locale here will break this component
     *
     * @type {string}
     */
    locale: {
      type: String,
      required: true
    },
    /**
     * Binding value of v-model
     *
     * @type {Array}
     */
    values: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      maxWidth: undefined,
      isMerged: false,
      dots: {
        firstDot: {
          isDraggingStart: false,
          xPad: 0,
          xPos: undefined,
          currentVal: undefined
        },
        lastDot: {
          isDraggingStart: false,
          xPad: 0,
          xPos: undefined,
          currentVal: undefined
        }
      },
      slider: {
        width: undefined,
        leftPad: 0
      },
      stepValues: undefined
    }
  },
  watch: {
    minVal: function () {
      this._updateUi()
    },
    maxVal: function () {
      this._updateUi()
    },
    values: function () {
      this._updateUi()
    }
  },
  created() {
    globalThis.addEventListener?.('mousemove', this.draggingMove)
    globalThis.addEventListener?.('mouseup', this.draggingStop)
    globalThis.addEventListener?.('touchmove', this.draggingMove)
    globalThis.addEventListener?.('touchend', this.draggingStop)
  },
  destroyed() {
    globalThis.removeEventListener?.('mousemove', this.draggingMove)
    globalThis.removeEventListener?.('mouseup', this.draggingStop)
    globalThis.removeEventListener?.('touchmove', this.draggingMove)
    globalThis.removeEventListener?.('touchend', this.draggingStop)
  },
  mounted() {
    this._updateUi(true)
  },
  methods: {
    /**
     * This function is to set the current location of the dot when it is clicked / pressed.
     *  - isDraggingStart: is to indicate which dot is ready to be moved
     *  - we do this because we allow to drag the dot left-right even if the cursor is not on the dot itself
     *
     * @param {string} loc - the dot location. This is the key of this.dots
     * @param {Event} evnt - the event object
     */
    draggingStart: function (loc, evnt) {
      this.dots[loc].isDraggingStart = true
      const xPos = this._xPos(evnt)

      if (!this.dots[loc].xPos) {
        this.dots[loc].xPos = xPos
      }
    },
    /**
     * This function is triggered when a move event start is triggered
     *  - this will not be triggered if neither dots are in the 'isDraggingStart' state because the listener fire this function every time the move event start is triggered
     * This function does the following:
     *  - trigger either the _processFirstDot or _processLastDot function to determine the dot position
     *  - trigger _processSlider function to process the width & position of the red line
     *  - trigger _processMerging function to check if the tooltip should be merged
     *
     * @param {Event} evnt - the event object
     */
    draggingMove: function (evnt) {
      if (
        this.dots.firstDot.isDraggingStart === false &&
        this.dots.lastDot.isDraggingStart === false
      )
        return

      const xPos = this._xPos(evnt)

      const key = Object.keys(this.dots).find(
        (key) => this.dots[key].isDraggingStart
      )

      this._processDot(xPos, key)

      this._processSlider()
      this._processMerging()
    },
    /**
     * This function is triggered when a move event end is triggered
     * This function does the following:
     *  - set all dots 'isDraggingStart' to false to stop the draggingMove to be fired
     *  - sort the current values of both dots to ensure its sorted in ascending order
     *  - emit the values of both dots: [minValue, maxValue]
     */
    draggingStop: function () {
      if (
        this.dots.firstDot.isDraggingStart === false &&
        this.dots.lastDot.isDraggingStart === false
      )
        return

      this.dots.firstDot.isDraggingStart = false
      this.dots.lastDot.isDraggingStart = false

      const currentVal = [
        this.dots.firstDot.currentVal,
        this.dots.lastDot.currentVal
      ].sort((a, b) => a - b)

      this.$emit('current-value', currentVal)
    },
    /**
     * This function is to return the formatted label of the dot's tooltip
     *
     * @param {string} loc - the dot location. This is the key of this.dots
     * @returns {string} the formatted dot label for the tooltip
     */
    processDotLabel: function (loc) {
      return `${this.labelPrefix}${this._getFormattedVal(
        this.dots[loc].currentVal,
        this.locale
      )}${this.labelSuffix}`
    },
    /**
     * This function is to return the formatted label of the merged dot's tooltip
     * There are 2 use cases:
     *  - return 'xx - yy' if both dots' values are not equal
     *  - return 'xx' if both dots' values are equal
     *
     * @returns {string} the formatted merge dot label for the tooltip
     */
    processMergedLabel: function () {
      if (this.dots.firstDot.currentVal === this.dots.lastDot.currentVal) {
        return `${this.processDotLabel('firstDot')}`
      }

      if (this.dots.firstDot.currentVal > this.dots.lastDot.currentVal) {
        return `${this.processDotLabel('lastDot')} - ${this.processDotLabel(
          'firstDot'
        )}`
      }

      return `${this.processDotLabel('firstDot')} - ${this.processDotLabel(
        'lastDot'
      )}`
    },
    /**
     * This function is to determine if the merged tooltip should be displayed
     *  If both dot's tooltip intersect, display the merged tooltip
     */
    _processMerging: function () {
      const firstDotSliderTooltip = this.$refs[
        'first-dot-slider-tooltip'
      ].getBoundingClientRect()
      const lastDotSliderTooltip = this.$refs[
        'last-dot-slider-tooltip'
      ].getBoundingClientRect()

      const isIntersect = this._isIntersectRect(
        firstDotSliderTooltip,
        lastDotSliderTooltip
      )

      this.isMerged = isIntersect
    },
    /**
     * This function is to return the number in the format that is localized
     *
     * @param {number} val - number to be humanized
     * @param {string} locale - format: language_countryCodeInUppercase (eg: en_ID) or just country code (uppercase / lowercase)
     * @returns {string} the formatted number
     */
    _getFormattedVal: function (val, locale) {
      locale = locale.replace('_', '-')
      return new Intl.NumberFormat(locale).format(val)
    },
    /**
     * This function is to determine the width & left padding of the slider
     */
    _processSlider: function () {
      this.slider.width = Math.abs(
        this.dots.firstDot.xPad - this.dots.lastDot.xPad
      )

      this.slider.leftPad =
        this.dots.firstDot.currentVal < this.dots.lastDot.currentVal
          ? this.dots.firstDot.xPad
          : this.dots.lastDot.xPad
    },
    /**
     * This function is to check if 2 rectangle is intersected or not
     *  - both object (rect1 & rect2) has the following structure: { top: Number, right: Number, bottom: Number, left: Number }
     *
     * @param {object} rect1 - used here for the left tooltip
     * @param {object} rect2 - used here for the right tooltip
     * @returns {boolean} the formatted dot label for the tooltip
     */
    _isIntersectRect: function (rect1, rect2) {
      return !(
        rect2.left > rect1.right ||
        rect2.right < rect1.left ||
        rect2.top > rect1.bottom ||
        rect2.bottom < rect1.top
      )
    },

    /**
     * This function is to set the dot:
     *  - current value
     *  - current position by x padding (this will be used in the CSS transform) in px
     *
     * @param {number} xPos - x coordinate of the Event object
     * @param {string} whichDot - 2 values: firstDot / lastDot
     * @param {boolean} isForcePos - to force to use xPos instead of processing
     * @param {boolean} isForceVal - to force to use dot.currentVal instead of processing
     */
    _processDot: function (
      xPos,
      whichDot,
      isForcePos = false,
      isForceVal = false
    ) {
      const sliderLine = this.$refs['slider-line'].getBoundingClientRect()
      const sliderLeft = sliderLine.left

      let finalXPos = xPos - sliderLeft

      if (isForcePos) {
        finalXPos = xPos
      }

      if (finalXPos < 0) {
        finalXPos = 0
      } else if (finalXPos > this.maxWidth) {
        finalXPos = this.maxWidth
      }

      const dot = this.dots[whichDot]
      dot.xPad = finalXPos

      if (!isForceVal) {
        dot.currentVal = this.stepValues.getCurrentVal(finalXPos)
      }
    },
    /**
     * This function is to get the x coordinate from the event
     * This is its own function to handle touch & mouse events
     *
     * @param {Event} evnt - Event object from touch / click
     * @returns {number} x coordinate of the Event object
     */
    _xPos: function (evnt) {
      const touchEventTypes = [
        'touchstart',
        'touchmove',
        'touchend',
        'touchcancel'
      ]
      const mouseEventTypes = [
        'mousedown',
        'mouseup',
        'mousemove',
        'mouseover',
        'mouseout',
        'mouseenter',
        'mouseleave'
      ]
      if (touchEventTypes.includes(evnt.type)) {
        return (evnt.touches[0] || evnt.changedTouches[0]).pageX
      } else if (mouseEventTypes.includes(evnt.type)) {
        return evnt.x
      }
    },

    async _updateUi(isInitial = false) {
      this.maxWidth = this.$refs['slider-line'].clientWidth
      this.slider.width = this.maxWidth

      this.stepValues = new StepValues(
        this.minVal,
        this.maxVal,
        this.step,
        this.maxWidth
      )

      const sortedValues = [...this.values].sort((a, b) => a - b)

      this.dots.firstDot.currentVal = this._getFirstDotVal(
        sortedValues[0],
        this.dots.firstDot.currentVal,
        this.minVal
      )
      this.dots.lastDot.currentVal = this._getLastDotVal(
        sortedValues[1],
        this.dots.lastDot.currentVal,
        this.maxVal
      )

      const totalFirstPos = this.stepValues.getCurrentWidth(sortedValues[0])
      const totalLastPos = this.stepValues.getCurrentWidth(sortedValues[1])

      this._processDot(totalFirstPos, 'firstDot', true, isInitial)
      this._processDot(totalLastPos, 'lastDot', true, isInitial)
      await this._processSlider() // This is to wait for the DOM to process first. Why this works like this? Not sure
      this._processMerging()
    },

    _getFirstDotVal(inputVal, currentVal, minVal) {
      let val

      if (currentVal >= minVal) {
        val = currentVal
      } else if (inputVal >= minVal) {
        val = inputVal
      } else {
        val = minVal
      }

      return val
    },

    _getLastDotVal(inputVal, currentVal, maxVal) {
      let val

      if (currentVal <= maxVal) {
        val = currentVal
      } else if (inputVal <= maxVal) {
        val = inputVal
      } else {
        val = maxVal
      }

      return val
    }
  }
}
</script>

<style lang="scss" scoped>
.slider {
  --color-active: #{$red};

  padding: 12px;
  width: calc(100% - 12px - 12px);
  &-line {
    height: 4px;
    background-color: $grey;
    position: relative;
    border-radius: 15px;
  }
  &-dot-container {
    position: absolute;
    transition: all 0s;
    will-change: transform;
    transition-duration: 0s;
    z-index: 1;
    cursor: pointer;
    width: 24px;
    height: 24px;
    top: -10px;
    left: -12px;
    @mixin draggingStyles {
      background-color: var(--color-active);
      border-color: var(--color-active);
    }
    &:hover {
      .slider-dot {
        @include draggingStyles;
      }
    }
    &.dragging {
      z-index: 2;
      .slider-dot {
        @include draggingStyles;
      }
    }
  }
  &-dot {
    background-color: $grey-light;
    border: 1px solid $grey;
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }
  &-tooltip {
    position: absolute;
    top: -4px;
    left: 50%;
    transform: translate(-50%, -100%);
    background-color: $grey-darker;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    padding: 2px 5px;
    color: $white;
    min-width: 20px;
    text-align: center;
    border-radius: 3px;
    &.tooltip-merge {
      top: -14px;
      display: none;
    }
  }
  &.merged {
    .slider-tooltip {
      visibility: hidden;
    }
    .tooltip-merge {
      display: block;
      visibility: visible;
    }
  }
  &-process {
    background-color: var(--color-active);
    width: 100%;
    height: 100%;
    will-change: width;
    position: absolute;
  }
}
</style>
